import { createHttpClient } from '@shared/adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { ProductLocationsApiAdapter } from '../adapters/ProductLocationsApiAdapter';
import type { IdentifiedOrder } from '../models';
import { extractProductIdsFromIdentifiedOrder } from '../utils/extractProductIdsFromIdentifiedOrder';

export type ReceiveLocationChip = {
  id: number;
  code: string;
  stock: number;
};

export type LocationsByProductId = Record<number, ReceiveLocationChip[]>;

export function useReceiveProductLocations(order: IdentifiedOrder | null) {
  const productIds = useMemo(() => (order ? extractProductIdsFromIdentifiedOrder(order) : []), [order]);

  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const adapter = useMemo(() => new ProductLocationsApiAdapter(httpClient), [httpClient]);

  const idsKey = productIds.join(',');

  const query = useQuery({
    queryKey: ['reused-receive-product-locations', idsKey],
    queryFn: () => adapter.getByProductIds(productIds),
    enabled: productIds.length > 0 && order != null,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const locationsByProductId = useMemo((): LocationsByProductId => {
    const map: LocationsByProductId = {};
    for (const loc of query.data ?? []) {
      const pid = loc.product_id;
      if (!map[pid]) map[pid] = [];
      map[pid].push({
        id: loc.id,
        code: loc.code,
        stock: loc.stock,
      });
    }
    return map;
  }, [query.data]);

  return {
    locationsByProductId,
    isLoadingLocations: query.isLoading,
    locationsError: query.error,
  };
}
